import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { SectionEyebrow } from './SectionEyebrow';
import { Reveal } from './Reveal';
import { GridRails } from './GridRails';
import { DitherImage } from './DitherImage';
import { principles, Principle, PrinciplePoint } from '../data/home';
import { ECOSYSTEM_SELECT_EVENT } from '../data/ecosystem';
const LINE = 'border-cream/10';
// Same as the hero nav links: Space Mono, 11px, caps, 0.08em tracking, softened cream
const TILE_TITLE = 'font-accent text-[11px] font-normal uppercase leading-[1.4] tracking-[0.08em] text-cream/[0.68]';

/** A grid of small outlined tiles sharing their borders — one label, one short line each. */
function PointTiles({
  points,
  tall
}: {
  points: PrinciplePoint[];
  tall?: boolean;
}) {
  return <div className={`grid grid-cols-1 border-l border-t sm:grid-cols-2 ${LINE}`} data-id="element-374" data-mp="29o6zoyaovgs6">
      {points.map(pt => <div key={pt.title} className={`flex flex-col justify-between gap-3 border-b border-r px-5 py-5 md:gap-5 md:px-6 md:py-6 ${LINE} ${tall ? 'sm:min-h-[200px]' : 'sm:min-h-[140px]'}`} data-id="element-375" data-mp="24x71zqtqwuom">
          <h4 className={TILE_TITLE} data-id="element-376" data-mp="y051dgvjtei4">{pt.title}</h4>
          <p className="max-w-[240px] text-[14.5px] leading-[1.45] text-cream/60" data-id="element-377" data-mp="jca7hx5t143g">{pt.body}</p>
        </div>)}
    </div>;
}
function ArrowGlyph() {
  return <span className="relative inline-flex h-[0.7em] w-[0.7em] overflow-hidden align-middle" data-id="element-378" data-mp="o9sfnlbzzfae">
      <ArrowUpRightIcon aria-hidden="true" strokeWidth={1.25} className="tile-arrow h-full w-full group-hover:-translate-y-full group-hover:translate-x-full" data-id="element-379" data-mp="aa0zbdy0dmt8" />
      <ArrowUpRightIcon aria-hidden="true" strokeWidth={1.25} className="tile-arrow absolute inset-0 h-full w-full -translate-x-full translate-y-full group-hover:translate-x-0 group-hover:translate-y-0" data-id="element-380" data-mp="eprzgoxjhvi" />
    </span>;
}

/** Linked pillar tile: lighter navy panel, dotted artwork, dot-lens + corner-tick hover. */
function PillarTile({
  p,
  large
}: {
  p: Principle;
  large?: boolean;
}) {
  return <a href={p.href}
  // Tells the Ecosystem section which pillar slide to show as the page scrolls there
  onClick={() => window.dispatchEvent(new CustomEvent(ECOSYSTEM_SELECT_EVENT, {
    detail: p.title
  }))} className={`pillar-cell group relative flex flex-col justify-between overflow-hidden bg-[#1a2945] p-6 md:p-10 ${large ? 'min-h-[340px] md:min-h-[480px]' : 'min-h-[200px] md:min-h-[240px]'}`} data-id="element-381" data-mp="1y7a6abhw0z2d">
      <span aria-hidden="true" className="pillar-art pointer-events-none absolute inset-0" data-id="element-382" data-mp="2c09lsdho6t9x">
        <DitherImage interactive src={p.image} className={large ? '-right-[10%] -top-[4%] h-[80%] w-[78%] [mask-image:radial-gradient(closest-side,#000_60%,transparent)]' : '-right-[6%] top-0 h-full w-[60%] [mask-image:radial-gradient(closest-side,#000_55%,transparent)]'} data-id="element-383" data-mp="279je2tlruec2" />
      </span>

      {large ? <>
          <h3 className="pillar-title relative flex items-center gap-5 font-display text-[46px] leading-none tracking-[-0.025em] md:text-[72px]" data-id="element-384" data-mp="hhrgszgsqlkh">
            {p.title}
            <ArrowGlyph data-id="element-385" data-mp="qhnxadt18e4g" />
          </h3>
          <p className="pillar-kicker relative mt-10 max-w-[460px] md:mt-16 text-[17px] leading-[1.45]" data-id="element-386" data-mp="qgmlu883tpm7">{p.kicker}</p>
        </> : <>
          <p className="pillar-kicker relative max-w-[420px] text-[17px] leading-[1.4]" data-id="element-387" data-mp="veefp7sb7lt1">{p.kicker}</p>
          <h3 className="pillar-title relative mt-6 flex items-center gap-4 font-display text-[38px] md:mt-10 md:text-[44px] leading-none tracking-[-0.02em] md:text-[52px]" data-id="element-388" data-mp="15eyjah3ax2ef">
            {p.title}
            <ArrowGlyph data-id="element-389" data-mp="1a81686dkpdln" />
          </h3>
        </>}
    </a>;
}
export function PrinciplesSection() {
  const [featured, ...rest] = principles;
  return <section id="principles" className="relative bg-navy text-cream" data-id="element-390" data-mp="1j7wjcgqoviob">
      <GridRails tone="dark" data-id="element-391" data-mp="1o4n0l7e8ckbb" />
      <div className="relative z-10 mx-auto max-w-[1720px] px-9 pb-[56px] pt-[56px] md:px-24 md:pb-[120px] md:pt-[110px] lg:px-32" data-id="element-392" data-mp="1o5xzasstj1vk">
        <Reveal data-id="element-393" data-mp="15jmf55pedu0f">
          <div className={`border-b pb-6 ${LINE}`} data-id="element-394" data-mp="15k5xuomvgmfi">
            <SectionEyebrow label="Pillars" tone="dark" data-id="element-395" data-mp="15jcorh0fxku7" />
          </div>
          <div className="mt-6 grid gap-4 md:mt-10 md:grid-cols-[1.4fr_1fr] md:gap-8 md:items-end" data-id="element-396" data-mp="1k7hbcw2m6gi9">
            <h2 className="max-w-[760px] font-display text-[26px] leading-[1.13] tracking-[-0.015em] md:text-[32px]" data-id="element-397" data-mp="1faqtc3791au7">
              Financial assets, media reach and human performance —{' '}
              <span className="font-editorial italic tracking-[-0.01em]" data-id="element-398" data-mp="mltop2vyncjx">compounding</span> one another.
            </h2>
            <p className="max-w-[380px] text-[14.5px] leading-[1.5] text-cream/60 md:justify-self-end md:pb-3" data-id="element-399" data-mp="11jlzxiuh5esb">
              Three domains, one standard. Each strengthens the others across the ecosystem.
            </p>
          </div>
        </Reveal>

        {/* Bento: featured Culture tile + its points */}
        <Reveal className="mt-10 grid gap-3 md:mt-[90px] md:gap-4 lg:grid-cols-2" data-id="element-400" data-mp="27xtrgu80jjjx">
          <PillarTile p={featured} large data-id="element-401" data-mp="2fg1azcuugr77" />
          <PointTiles points={featured.points} tall data-id="element-402" data-mp="2kgpdauoiwugq" />
        </Reveal>

        {/* Capital and Longevity: feature tile above their point tiles */}
        <div className="mt-3 grid gap-3 md:mt-4 md:gap-4 lg:grid-cols-2" data-id="element-403" data-mp="26i95f0excxx0">
          {rest.map((p, i) => <Reveal key={p.title} delay={i * 0.1} className="flex flex-col" data-id="element-404" data-mp="2aj1ffvvmf6uc" data-mpi={i}>
              <PillarTile p={p} data-id="element-405" data-mp="2y956l3rmhe4g" />
              <PointTiles points={p.points} data-id="element-406" data-mp="2y8vpk5ok2haw" />
            </Reveal>)}
        </div>
      </div>
    </section>;
}